/**
 * SpatialCursor - 3D cursor (double rings, smooth fade)
 * Fixed: Hover works over hollow center of rings
 */

import * as THREE from 'three';

export class SpatialCursor {
  constructor(scene, camera, geometryModel) {
    this.scene = scene;
    this.camera = camera;
    this.geometryModel = geometryModel;

    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();

    this.enabled = true;
    this.currentDepth = 0;
    this.currentNormal = new THREE.Vector3(0, 1, 0);

    this.createCursorMesh();
    this.setupEventListeners();
  }

  createCursorMesh() {
    const cursorGroup = new THREE.Group();
    cursorGroup.name = 'spatialCursor';

    // Cursor outer ring
    const outerInner = 0.12;
    const outerOuter = 0.17;

    // Cursor inner ring
    const innerInner = 0.085;
    const innerOuter = 0.1;

    const baseMat = new THREE.MeshBasicMaterial({
      color: 0xffffff,
      transparent: true,
      opacity: 0.65,
      side: THREE.DoubleSide,
      depthTest: false
    });

    // Outer ring (main ring)
    const outerGeometry = new THREE.RingGeometry(outerInner, outerOuter, 64);
    this.outerRing = new THREE.Mesh(outerGeometry, baseMat.clone());
    this.outerRing.material.opacity = 0.55;
    cursorGroup.add(this.outerRing);

    // Inner ring (thin ring inside)
    const innerGeometry = new THREE.RingGeometry(innerInner, innerOuter, 64);
    this.innerRing = new THREE.Mesh(innerGeometry, baseMat.clone());
    this.innerRing.material.opacity = 0.30;
    cursorGroup.add(this.innerRing);

    // --- NEW: Invisible circle collider for hollow center ---
    const colliderGeometry = new THREE.CircleGeometry(innerInner, 32);
    const colliderMat = new THREE.MeshBasicMaterial({ visible: false });
    this.collider = new THREE.Mesh(colliderGeometry, colliderMat);
    this.collider.rotateX(-Math.PI / 2); // Match cursor orientation
    cursorGroup.add(this.collider);
    // -------------------------------------------------------

    // Remove dot and depth line → remove them
    this.dot = null;
    this.depthLine = null;

    this.cursor = cursorGroup;
    this.cursor.visible = false;
    this.cursor.renderOrder = 999;

    this.scene.add(this.cursor);
  }

  setupEventListeners() {
    window.addEventListener('mousemove', this.onMouseMove.bind(this));
  }

  onMouseMove(event) {
    if (!this.enabled) return;

    this.mouse.x = (event.clientX / window.innerWidth) * 2 - 1;
    this.mouse.y = -(event.clientY / window.innerHeight) * 2 + 1;
  }

  update() {
    if (!this.enabled || !this.geometryModel) {
      this.cursor.visible = false;
      return;
    }

    this.raycaster.setFromCamera(this.mouse, this.camera);

    const geometryObjects = [];
    this.geometryModel.traverse((child) => {
      if (child.isMesh && child.userData.isGeometry) geometryObjects.push(child);
    });

    const intersects = this.raycaster.intersectObjects(geometryObjects, true);

    if (intersects.length === 0) {
      this.cursor.visible = false;
      return;
    }

    const hit = intersects[0];
    this.cursor.visible = true;

    // Position cursor
    this.cursor.position.copy(hit.point);

    // Surface normal → used for proper orientation
    const normal = hit.face.normal.clone();
    normal.transformDirection(hit.object.matrixWorld);
    this.currentNormal.copy(normal);

    // Orient cursor to surface normal
    this.cursor.lookAt(
      this.cursor.position.x + normal.x,
      this.cursor.position.y + normal.y,
      this.cursor.position.z + normal.z
    );

    // Distance & scaling
    this.currentDepth = hit.distance;
    const scale = Math.max(0.5, Math.min(2.0, this.currentDepth * 0.2));
    this.cursor.scale.set(scale, scale, scale);

    // Update appearance
    this.updateCursorAppearance(normal, this.currentDepth);
  }

  updateCursorAppearance(normal, distance) {
    this.outerRing.material.color.setHex(0xffffff);
    this.innerRing.material.color.setHex(0xffffff);

    const maxDist = 6.0;
    const fade = Math.max(0.25, 1.0 - distance / maxDist);

    this.outerRing.material.opacity = 0.55 * fade;
    this.innerRing.material.opacity = 0.30 * fade;
  }

  isOverHotspot(hotspots) {
    if (!this.cursor.visible || !hotspots) return null;

    const threshold = 0.5;
    for (const hotspot of hotspots) {
      if (hotspot.position.distanceTo(this.cursor.position) < threshold) {
        return hotspot;
      }
    }
    return null;
  }

  setInteractiveMode(isInteractive) {
    const color = isInteractive ? 0xffffff : 0xffffff;

    this.outerRing.material.color.setHex(color);
    this.innerRing.material.color.setHex(color);
    this.cursor.scale.set(isInteractive ? 1.15 : 1, isInteractive ? 1.15 : 1, isInteractive ? 1.15 : 1);
  }

  setEnabled(enabled) {
    this.enabled = enabled;
    if (!enabled) this.cursor.visible = false;
  }

  getPosition() {
    return this.cursor.position.clone();
  }

  getNormal() {
    return this.currentNormal.clone();
  }

  dispose() {
    window.removeEventListener('mousemove', this.onMouseMove);
    this.scene.remove(this.cursor);

    this.outerRing.geometry.dispose();
    this.outerRing.material.dispose();

    this.innerRing.geometry.dispose();
    this.innerRing.material.dispose();

    if (this.collider) {
      this.collider.geometry.dispose();
      this.collider.material.dispose();
    }
  }
}
